import { useMemo, useState } from 'react';

/**
 * Column-config hook shared by V3 reports (item-wise, daywise, …).
 *
 * Tracks which columns are visible, preserves the catalogue order, and
 * derives the matching export-column descriptors and totals keys. Matches
 * the original item-wise behaviour: in-session only, no persistence —
 * the selection resets on reload.
 *
 *   allColumns   — full catalogue:
 *                  [{ key, label, align?, isMoney?, isCount?, format?, total? }]
 *   defaultKeys  — array of column keys visible by default
 *
 * Returns:
 *   selectedColumns / setSelectedColumns — visible-key state
 *   onChange        — MultiSelect handler (falls back to defaults if emptied)
 *   columnOptions   — [{ value, label }] for the picker
 *   visibleColumns  — allColumns filtered to selection, original order
 *   exportColumns   — [{ key, header, isMoney, isCount, total }] for export
 *   totalKeys       — keys whose sum is meaningful (col.total === true)
 */
export default function useColumnConfig(allColumns, defaultKeys) {
    const [selectedColumns, setSelectedColumns] = useState(defaultKeys);

    const columnOptions = useMemo(
        () => allColumns.map((c) => ({ value: c.key, label: c.label })),
        [allColumns],
    );

    // Visible columns preserve the catalogue order regardless of pick order.
    const visibleColumns = useMemo(
        () => allColumns.filter((c) => selectedColumns.includes(c.key)),
        [allColumns, selectedColumns],
    );

    const exportColumns = useMemo(
        () => visibleColumns.map((c) => ({
            key: c.key,
            header: c.label,
            isMoney: c.isMoney === true,
            isCount: c.isCount === true,
            total: c.total === true,
        })),
        [visibleColumns],
    );

    const totalKeys = useMemo(
        () => allColumns.filter((c) => c.total).map((c) => c.key),
        [allColumns],
    );

    // Never allow an empty table — fall back to defaults when fully cleared.
    const onChange = (next) => setSelectedColumns(next.length ? next : defaultKeys);

    return {
        selectedColumns,
        setSelectedColumns,
        onChange,
        columnOptions,
        visibleColumns,
        exportColumns,
        totalKeys,
    };
}
